import { MutationTree, GetterTree, ActionTree } from "vuex";
import { RootState } from "vuefront-store";
import { Cart } from "vuefront-api";
import find from "lodash/find";
import {
  formatGraphQLError,
  handleMutationResult,
} from "../../common/errors/handleStoreError";
import { CartMutationPayload, CartQueryData } from "./cart.graphql-types";
import {
  addToCart as addToCartMutation,
  updateCart as updateCartMutation,
  removeCart as removeCartMutation,
  cart as cartQuery,
} from "./graphql/cart.graphql";
import { resetGiftOrderPreference } from "~/core/utils/giftOrder";

export type State = {
  cart: Cart;
};

export const state = (): State => ({
  cart: {
    products: [],
  },
});

export const getters: GetterTree<State, RootState> = {
  get(state) {
    return state.cart;
  },
};

interface Option {
  id: string;
}

interface CartSubTotal {
  code: String;
  title: String;
  value: String;
  data: {
    coupon_code: String;
    type: String;
    rate: String;
    sort_order: String;
  };
}

export interface CartItem {
  product: {
    id: string;
  };
  option: Option[];
  quantity: number;
}

export const mutations: MutationTree<State> = {
  setCart(
    state,
    cart: {
      products?: CartItem[];
      total?: number | string;
      subtotal?: string;
      totals?: CartSubTotal[];
    }
  ) {
    state.cart = {
      ...state.cart,
      products: cart.products || [],
      total: typeof cart.total === "undefined" ? state.cart.total : cart.total,
      subtotal:
        typeof cart.subtotal === "undefined"
          ? state.cart.subtotal
          : cart.subtotal,
      totals:
        typeof cart.totals === "undefined" ? state.cart.totals : cart.totals,
    };
  },

  addToCart(state, cartItem: CartItem) {
    const existingIndex = state.cart.products.findIndex((item: CartItem) => {
      const isSameProduct =
        item.product.id === cartItem.product.id &&
        item.option.length === cartItem.option.length &&
        cartItem.option.every((opt, index) => opt.id === item.option[index].id);
      return isSameProduct;
    });

    if (existingIndex !== -1) {
      // state.cart.products[existingIndex].quantity += cartItem.quantity;
      state.cart.products[existingIndex].quantity = cartItem.quantity;
      state.cart.products[existingIndex].total = calculateTotal(
        state.cart.products[existingIndex].product,
        state.cart.products[existingIndex].quantity
      );
    } else {
      state.cart.products.push(cartItem);
    }
  },

  removeCartItem(state, key: string) {
    const index = state.cart.products.findIndex(
      (cartItem: CartItem & { key?: string }) => cartItem.key === key
    );

    if (index !== -1) {
      state.cart.products.splice(index, 1);
    }
  },

  updateCartItem(state, updatedItem) {
    const updatedProducts = state.cart.products.map((item) => {
      if (item.key === updatedItem.key) {
        return { ...item, ...updatedItem };
      } else {
        return item;
      }
    });
    state.cart.products = updatedProducts;
  },
};

interface Product {
  price: string;
  original_price: string;
}

const calculateTotal = (product: Product, quantity: number) => {
  const productPrice = parseFloat(
    product.price.replace("$", "").replace(",", "")
  );

  if (isNaN(productPrice)) {
    return "The price is invalid";
  }

  const total: number = quantity * productPrice;
  const roundedTotal: number = Math.ceil(total);
  const formattedTotal: string = total.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return formattedTotal;
};

const isConfirmedEmptyCart = (cart: { products?: unknown[] } | null) =>
  Array.isArray(cart?.products) && cart.products.length === 0;

export const actions: ActionTree<State, RootState> = {
  async add(
    { commit, dispatch, rootGetters },
    { product, quantity = 1, options = [], redirect = false }
  ) {
    if (Array.isArray(product)) {
      const results = [];
      for (let prod of product) {
        const existingProduct = rootGetters["vuefront/cart/products"]?.find(
          (item: CartItem) =>
            item.product.id === prod.id &&
            item.option.every(
              (opt, index) => options[index] && opt.id === options[index].id
            )
        );

        if (existingProduct) {
          results.push(
            await dispatch("update", {
              key: existingProduct.key,
              quantity: existingProduct.quantity + quantity,
            })
          );
        } else {
          results.push(
            await dispatch("addToCart", {
              product: prod,
              quantity,
              options,
              redirect,
            })
          );
        }
      }
      return results;
    } else {
      const existingProduct = rootGetters["vuefront/cart/products"]?.find(
        (item: CartItem) =>
          item.product.id === product.id &&
          item.option.every(
            (opt, index) => options[index] && opt.id === options[index].id
          )
      );

      if (existingProduct) {
        return dispatch("update", {
          key: existingProduct.key,
          quantity: existingProduct.quantity + quantity,
        });
      } else {
        return dispatch("addToCart", { product, quantity, options, redirect });
      }
    }
  },

  async addToCart(
    { commit, dispatch, rootGetters },
    { product, quantity = 1, options = [], redirect = false }
  ) {
    const response = await dispatch(
      "apollo/mutate",
      {
        mutation: addToCartMutation,
        variables: {
          id: product.id,
          quantity,
          options,
        },
        invalidateCache: true,
      },
      {
        root: true,
      }
    );

    // Catalog prefetches can replace the shared apollo/get while cache
    // invalidation is awaited. Use this mutation's own response instead.
    const payload: CartMutationPayload | undefined = response?.addToCart;
    if (payload && Array.isArray(payload.products)) {
      // The mutation returns the authoritative complete cart. Replacing the
      // product list also restores older server-side lines that a stale SSR
      // snapshot may not have contained.
      commit("setCart", payload);

      if (!rootGetters["vuefront/sidebarCart"]) {
        commit("vuefront/toggleSidebarCart", {}, { root: true });
      }
      return { ok: true, cart: payload };
    }
    handleMutationResult({ commit, rootGetters }, { showNotification: true });
    if (redirect) {
      this.$router.push("/store/product/" + product.id);
    }
    return {
      ok: false,
      error: {
        code: "PRODUCT_UNAVAILABLE",
        message:
          formatGraphQLError(rootGetters["vuefront/error"]) ||
          "This item could not be added to your bag.",
      },
    };
  },

  async update({ commit, dispatch, rootGetters, state }, { key, quantity }) {
    // const existingProduct = rootGetters["vuefront/cart/products"].find(
    //   (cartItem: CartItem & { key?: string }) => cartItem.key === key
    // );

    // `state` from the action context (was the module-level export before —
    // part of the SSR cross-request state-sharing bug).
    const existingProduct = state.cart.products.find(
      (cartItem: CartItem & { key?: string }) => cartItem.key === key
    );

    // Snapshot for rollback: the quantity/total update below is optimistic
    // (committed before the server responds).
    const previousProduct = existingProduct ? { ...existingProduct } : null;

    if (existingProduct) {
      const updatedProduct = {
        ...existingProduct,
        quantity: quantity,
        total: calculateTotal(existingProduct.product, quantity),
      };
      commit("updateCartItem", updatedProduct);
    } else {
      console.error("Product not found in cart");
    }

    await dispatch(
      "apollo/mutate",
      {
        mutation: updateCartMutation,
        variables: {
          key,
          quantity,
        },
        invalidateCache: true,
      },
      {
        root: true,
      }
    );

    const success = handleMutationResult(
      { commit, rootGetters },
      { showNotification: true }
    );
    if (success) {
      const updatedCart = rootGetters["apollo/get"].updateCart;
      commit("setCart", updatedCart);
      if (isConfirmedEmptyCart(updatedCart)) resetGiftOrderPreference();
      return { ok: true, cart: updatedCart };
    } else if (previousProduct) {
      commit("updateCartItem", previousProduct);
    }
    return {
      ok: false,
      error: {
        code: "PRODUCT_UNAVAILABLE",
        message:
          formatGraphQLError(rootGetters["vuefront/error"]) ||
          "This item could not be added to your bag.",
      },
    };
  },

  async removeByProductId({ dispatch, getters }, { productId }) {
    const product = find(
      getters.get.products,
      (o) => o.product.id === productId
    );
    if (product) {
      await dispatch("remove", { key: product.key });
    }
  },

  async remove({ commit, dispatch, rootGetters, state }, { key }) {
    const response = await dispatch(
      "apollo/mutate",
      {
        mutation: removeCartMutation,
        variables: {
          key,
        },
        invalidateCache: true,
      },
      {
        root: true,
      }
    );

    // Judge this mutation by its own response, as addToCart does: the shared
    // `vuefront/error` flag can be reset by a concurrent query.
    const payload: CartMutationPayload | undefined = response?.removeCart;
    if (payload && Array.isArray(payload.products)) {
      // `removed` is true only for the call that took the line out of the
      // local cart, so a repeated click on a pending removal is not counted
      // as a second removal.
      const removed =
        state.cart.products.some(
          (cartItem: CartItem & { key?: string }) => cartItem.key === key
        ) && !payload.products.some((line) => line.key === key);
      commit("removeCartItem", key);
      if (isConfirmedEmptyCart(state.cart)) resetGiftOrderPreference();
      return { ok: true, removed, cart: payload };
    }
    handleMutationResult({ commit, rootGetters }, { showNotification: true });
    return { ok: false, removed: false };
  },

  async load({ commit, dispatch, rootGetters }) {
    try {
      const { data }: { data: CartQueryData } = await this.$vfapollo.query({
        fetchPolicy: "network-only",
        query: cartQuery,
      });

      commit("setCart", data.cart);
      if (isConfirmedEmptyCart(data.cart)) resetGiftOrderPreference();
      return { ok: true, cart: data.cart };
    } catch (e) {
      commit("vuefront/setResponseError", e, {
        root: true,
      });
      return { ok: false };
    }
  },
};
